<template>
  <div class="create-tree-page">
    <div class="page-content container-lg">
      <section class="build-section">
        <h1>
          <img src="@/assets/icons/tree_start_150.png"/>
          Start your tree
        </h1>
        <div class="instructions">
          Start your family tree by using a <strong>genealogy app</strong> and entering what you know.
          Then use <strong>online records</strong> to find out more.
          Many popular apps and websites can help you get started.
        </div>
        <div class="row main-content">
          <div class="col-md-6 apps-area">
            <div>
              <h3>Genealogy apps</h3>
              <h5>Use one of these to start building your family tree</h5>
            </div>
            <ul class="list-unstyled app-list">
              <li v-for="app in appsRef" :key="app.id">
                <a :href="app.infoUrl" class="link-dark" target="_blank" rel="noopener noreferrer">
                  <img :src="app.icon" :alt="app.product"/>
                  <div>{{ app.product }}</div>
                </a>
              </li>
            </ul>
            <div class="other-apps">
              <button type="button" class="btn btn-inline btn-link btn-sm" data-bs-toggle="collapse" data-bs-target="#otherAppsHelp" aria-expanded="false" aria-controls="moreApps">
                Can I use a different app?
              </button>
              <div id="otherAppsHelp" class="collapse">
                If you use one of these apps, you can share your tree directly on Tree Crossing without exporting from the app.
                We're working on support for other apps, too.
              </div>
            </div>
            <div class="divider"></div>
          </div>
          <div class="col-md-6 records-area">
            <h3>Online records</h3>
            <h5>Use these to find out more about your ancestors</h5>
            <ul class="list-unstyled records-list">
              <li v-for="site in recordSites" :key="site.id">
                <a :href="site.url" class="link-dark" target="_blank" rel="noopener noreferrer">
                  <img :src="site.icon" :alt="site.name"/>
                  <div>
                    <div class="site-name">{{ site.name }}</div>
                    <div class="site-desc">{{ site.desc }}</div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section class="share-section">
        <h1>Share what you find</h1>
        <div class="instructions">
          As soon as you have something to share, come back and we'll help you share <strong>your</strong> family tree online!
        </div>
        <img src="@/assets/upload_tree.png" alt="laptop with tree"/>
        <router-link class="btn btn-primary" to="/signup">Upload my tree</router-link>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.create-tree-page {
  flex-grow: 1;
  padding: 2rem 0;
  background-color: var(--page-bg-alt);
}

.page-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
}

section {
  width: 100%;
}

.build-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem 2rem;
}

h1 {
  font-size: 2rem;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }

  img {
    width: 60px;
  }
}

.instructions {
  margin-bottom: 1rem;
  max-width: 800px;
  padding: 0 1rem;
  text-align: center;

  @media (min-width: 768px) {
    padding: 0;
    font-size: 1.1rem;
  }
}

h3, h5 {
  margin: 0;
  margin-top: 0.5rem;
  text-align: center;
}

h5 {
  font-size: 1.1rem;
  color: #888;
}

.apps-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  .divider {
    @media (min-width: 768px) {
      position: absolute;
      top: 10%;
      right: 0;
      height: 80%;
      border-left: 2px solid #ddd;
    }
  }
}

ul.app-list {
  margin: 0 auto;
  max-width: 500px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem 0.5rem;
  
  li > a {
    width: 90px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
    font-size: 0.875rem;

    @media (min-width: 768px) {
      width: 100px;
    }

    img {
      width: 50px;
      border-radius: 6px;

      @media (min-width: 768px) {
        width: 60px;
      }
    }
  }
}

.other-apps {
  //margin: 0 1rem;
  width: 350px;
  font-size: 0.875rem;
}

ul.records-list {
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  margin-top: 1rem;

  li > a {
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    .site-name {
      font-weight: 400;
      font-size: 1.28rem;
    }

    .site-desc {
      color: #888;
      font-size: 0.875rem;
      max-width: 400px;
    }
      
    img {
      width: 60px;
      margin-top: 0.5rem;
    }
  }
}

.share-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  .instructions {
    max-width: 600px;
  }

  img {
    width: 300px;
    
    @media (min-width: 768px) {
      width: unset;
    }
  }

  button {
    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { workspaceTypeInfos } from '@/rd/ResearchDataModel'

const promoteApps = ['RootsMagic', 'Legacy', 'AncestralQuest', 'FamilyTreeMaker']

const appsRef = computed(() => workspaceTypeInfos.filter(t => promoteApps.includes(t.id)))
const recordSites = [
  {
    id: 'FamilySearch',
    name: 'FamilySearch',
    url: 'https://www.familysearch.org',
    icon: new URL('@/assets/logos/FamilySearch.png', import.meta.url).href,
    desc: 'Records from the United States, Canada, Europe, Latin America, and more',
  },
  {
    id: 'Ancestry',
    name: 'Ancestry',
    url: 'https://www.ancestry.com',
    icon: new URL('@/assets/logos/Ancestry.png', import.meta.url).href,
    desc: 'Records from the United States, Canada, Europe, Australia and more',
  },
  {
    id: 'MyHeritage',
    name: 'MyHeritage',
    url: 'https://www.myheritage.com',
    icon: new URL('@/assets/logos/MyHeritage.png', import.meta.url).href,
    desc: 'Records from Central and Eastern Europe, Scandinavia, Middle East, and more',
  },
  {
    id: 'AmericanAncestors',
    name: 'American Ancestors',
    url: 'https://www.americanancestors.org',
    icon: new URL('@/assets/logos/AmericanAncestors.png', import.meta.url).href,
    desc: 'Records from the United States, British Isles, continental Europe, and more',
  },
  {
    id: 'FindMyPast',
    name: 'FindMyPast',
    url: 'https://www.findmypast.com',
    icon: new URL('@/assets/logos/Findmypast.png', import.meta.url).href,
    desc: 'Records from the UK, Ireland, Australia, and former British colonies',
  },
]
</script>
